/**
 * MissingFieldsWarningModal — avviso NON bloccante per campi mancanti.
 * Mostra i campi che mancano e lascia due strade: tornare a completarli oppure
 * salvare comunque.
 *
 * Questo componente esisteva già ma non lo usava nessuno: ogni schermata se lo
 * era riscritto inline (accettazione turbina, registrazione cliente, e altre
 * ancora). Il testo era copiato parola per parola, le due scelte pure. Da qui
 * in poi è questo il posto: chi lo chiama decide titolo, sottotitolo, etichette
 * e z-index — non l'aspetto, che deve restare lo stesso ovunque.
 *
 * Le due azioni sono sempre nello stesso ordine: completare (consigliata, in
 * evidenza) e proseguire comunque.
 */

import { AlertTriangle } from 'lucide-react';

export interface MissingField {
  field: string;
  label: string;
}

/** Un campo mancante: stringa già pronta da mostrare, o {field, label}. */
export type CampoMancante = string | MissingField;

interface MissingFieldsWarningModalProps {
  isOpen: boolean;
  missingFields: CampoMancante[];
  /** Azione consigliata: riporta l'utente sui campi da compilare. */
  onClose: () => void;
  /** Prosegue accettando che i campi restino vuoti. */
  onProceed: () => void;
  title?: string;
  subtitle?: string;
  closeLabel?: string;
  proceedLabel?: string;
  /**
   * Alcune chiamate avvengono sopra modali già montati (l'accettazione apre
   * questa sopra il form turbina), quindi lo z-index non può essere fisso.
   */
  zIndex?: number;
  customerType?: 'company' | 'individual';
}

/** Suggerimento contestuale accanto al nome del campo. */
const SUFFISSO: Record<string, string> = {
  partitaIva: '(11 cifre)',
  codiceFiscale: '(16 caratteri)',
  email: '(per comunicazioni)',
};

export function getMissingRecommendedFields(data: any, customerType: string): MissingField[] {
  const missing: MissingField[] = [];

  // Campi consigliati per tutti
  if (!data.email || !data.email.trim()) {
    missing.push({ field: 'email', label: 'Email' });
  }

  // Per aziende
  if (customerType === 'company' || customerType === 'azienda') {
    if (!data.partitaIva && !data.partita_iva && !data.vat_number) {
      missing.push({ field: 'partitaIva', label: 'Partita IVA' });
    }
  }

  // Per privati
  if (customerType === 'individual' || customerType === 'privato') {
    if (!data.codiceFiscale && !data.codice_fiscale && !data.tax_code) {
      missing.push({ field: 'codiceFiscale', label: 'Codice Fiscale' });
    }
  }

  return missing;
}

export default function MissingFieldsWarningModal({
  isOpen,
  missingFields,
  onClose,
  onProceed,
  title = 'Campi consigliati mancanti',
  subtitle = 'Puoi salvare, ma ti consigliamo di completare',
  closeLabel = 'Completa ora',
  proceedLabel = 'Salva comunque',
  zIndex = 9999,
  customerType: _customerType,
}: MissingFieldsWarningModalProps) {
  if (!isOpen || missingFields.length === 0) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm"
      style={{ zIndex }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="bg-slate-800 border border-amber-500/30 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-600/20 to-orange-600/20 border-b border-amber-500/30 p-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">{title}</h3>
              <p className="text-sm text-amber-200/70 mt-1">{subtitle}</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-5">
          <ul className="space-y-2 mb-4 max-h-48 overflow-y-auto">
            {missingFields.map((f, i) => {
              const label = typeof f === 'string' ? f : f.label;
              const suffisso = typeof f === 'string' ? '' : SUFFISSO[f.field] || '';
              return (
                <li key={i} className="flex items-center gap-2 text-sm text-gray-200">
                  <span className="w-2 h-2 bg-amber-400 rounded-full flex-shrink-0" />
                  <span>{label}</span>
                  {suffisso && <span className="text-gray-500 text-xs">{suffisso}</span>}
                </li>
              );
            })}
          </ul>

          <p className="text-xs text-amber-200/40 italic mb-5">
            Ok, io continuo... però sono cazzi tuoi! 😅 Stiamo lavorando con Neuralink per il collegamento
            telepatico col cervello del cliente, ma al momento non è implementato.
            Per cui ti consigliamo di inserire il maggior numero di dati possibili per rintracciare il cliente.
          </p>

          {/* Azioni */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 px-4 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-medium transition-colors"
            >
              {closeLabel}
            </button>
            <button
              onClick={onProceed}
              className="flex-1 py-2.5 px-4 rounded-xl border border-amber-500/50 hover:bg-amber-600/20 text-amber-300 font-medium transition-colors"
            >
              {proceedLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
